import React from "react";
import {
  X,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  User,
} from "lucide-react";
import { getStatusColor, getStatusIcon, calculateDuration, getTwoInitials } from "../utils/leaveHelpers";

const DetailsModal = ({
  showDetailsModal,
  setShowDetailsModal,
  selectedLeave,
  isDarkMode,
  screenSize,
  fonts,
  spacing,
  theme,
  language,
  t,
}) => {
  if (!showDetailsModal || !selectedLeave) return null;

  const modalStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
    backdropFilter: "blur(5px)",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  const modalContentStyle = {
    background: isDarkMode
      ? "rgba(15, 23, 42, 0.95)"
      : "rgba(248, 250, 252, 0.98)",
    backdropFilter: "blur(25px)",
    WebkitBackdropFilter: "blur(25px)",
    border: isDarkMode
      ? "1px solid rgba(71, 84, 103, 0.3)"
      : "1px solid rgba(203, 213, 225, 0.2)",
    borderRadius:
      screenSize === "xs" ? "0rem" : screenSize === "sm" ? "1.25rem" : "1.5rem",
    padding: spacing.padding,
    width: screenSize === "xs" ? "100%" : "90%",
    maxWidth:
      screenSize === "xs" ? "100%" : screenSize === "sm" ? "35rem" : "43.75rem",
    margin: screenSize === "xs" ? "0" : "11.875rem auto",
    maxHeight: screenSize === "xs" ? "100vh" : "80vh",
    overflowY: "auto",
    direction: language === "ar" ? "rtl" : "ltr",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  const infoSectionStyle = {
    background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
    borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
    padding: spacing.padding,
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
    marginBottom: spacing.margin,
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
    color: isDarkMode ? "white" : "#1a1a1a",
    fontSize: fonts.subtitle,
    fontWeight: "600",
    marginBottom: spacing.margin,
    paddingBottom: screenSize === "xs" ? "0.5rem" : "0.75rem",
    borderBottom: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
    fontFamily: "'Cairo', sans-serif",
  };

  const infoGridStyle = {
    display: "grid",
    gridTemplateColumns:
      screenSize === "xs"
        ? "1fr"
        : screenSize === "sm"
        ? "1fr"
        : "repeat(auto-fit, minmax(18.75rem, 1fr))",
    gap: spacing.margin,
  };

  const infoItemStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: screenSize === "xs" ? "0.5rem" : "0.75rem",
    padding: screenSize === "xs" ? "0.75rem" : "1rem",
    background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
    borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
    border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.03)",
  };

  const infoLabelStyle = {
    color: isDarkMode ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
    fontSize: fonts.tiny,
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: "0.03125rem",
    marginBottom: "1rem",
    fontFamily: "'Cairo', sans-serif",
  };

  const infoValueStyle = {
    color: isDarkMode ? "white" : "#1a1a1a",
    fontSize: fonts.body,
    fontWeight: "500",
    lineHeight: "1.4",
    fontFamily: "'Cairo', sans-serif",
  };

  const StatusIcon = getStatusIcon(selectedLeave.status);
  const duration =
    selectedLeave.duration ??
    calculateDuration(selectedLeave.startDate, selectedLeave.endDate);

  const statusColor =
    selectedLeave.status === "approved"
      ? theme.colors.success
      : selectedLeave.status === "declined"
      ? theme.colors.danger
      : theme.colors.warning;

  return (
    <div style={modalStyle} onClick={() => setShowDetailsModal(false)}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: screenSize === "xs" ? "1.5rem" : "2rem",
            borderBottom: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            paddingBottom: screenSize === "xs" ? "0.75rem" : "1rem",
            flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
            gap: screenSize === "xs" ? "1rem" : "0",
          }}
        >
          <h2
            style={{
              color: isDarkMode ? "white" : "#1a1a1a",
              fontSize: screenSize === "xs" ? "1.25rem" : "1.5rem",
              fontWeight: "700",
              margin: 0,
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {t.details.title}
          </h2>
          <button
            style={{
              background: "none",
              border: "none",
              color: isDarkMode ? "white" : "#1a1a1a",
              cursor: "pointer",
              padding: "0.5rem",
              borderRadius: "0.5rem",
              transition: "background 0.2s",
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Leave Information Section */}
        <div style={infoSectionStyle}>
          <div style={sectionHeaderStyle}>
            <FileText size={screenSize === "xs" ? 16 : 18} />
            <span>{t.sections.leaveInfo}</span>
          </div>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <FileText size={screenSize === "xs" ? 14 : 16} />
              <div>
                <div style={infoLabelStyle}>{t.details.leaveType}</div>
                <div style={infoValueStyle}>
                  {t.leaveTypes[selectedLeave.leaveType || selectedLeave.type] ||
                    selectedLeave.leaveType ||
                    selectedLeave.type ||
                    "Unknown"}
                </div>
              </div>
            </div>
            <div style={infoItemStyle}>
              <CheckCircle size={screenSize === "xs" ? 14 : 16} />
              <div>
                <div style={infoLabelStyle}>{t.details.status}</div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {React.createElement(StatusIcon, {
                    size: 16,
                    color: getStatusColor(selectedLeave.status, theme, isDarkMode),
                  })}
                  <span
                    style={{
                      color: getStatusColor(selectedLeave.status, theme, isDarkMode),
                      fontSize: fonts.body,
                      fontWeight: "600",
                      textTransform: "capitalize",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {t.status[selectedLeave.status] || selectedLeave.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Date Information Section */}
        <div style={infoSectionStyle}>
          <div style={sectionHeaderStyle}>
            <Calendar size={screenSize === "xs" ? 16 : 18} />
            <span>{t.sections.dateInfo}</span>
          </div>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <Calendar size={screenSize === "xs" ? 14 : 16} />
              <div>
                <div style={infoLabelStyle}>{t.details.startDate}</div>
                <div style={infoValueStyle}>
                  {new Date(selectedLeave.startDate).toLocaleDateString(
                    language === "ar" ? "ar-EG" : "en-US"
                  )}
                </div>
              </div>
            </div>
            <div style={infoItemStyle}>
              <Calendar size={screenSize === "xs" ? 14 : 16} />
              <div>
                <div style={infoLabelStyle}>{t.details.endDate}</div>
                <div style={infoValueStyle}>
                  {new Date(selectedLeave.endDate).toLocaleDateString(
                    language === "ar" ? "ar-EG" : "en-US"
                  )}
                </div>
              </div>
            </div>
            <div style={infoItemStyle}>
              <Clock size={screenSize === "xs" ? 14 : 16} />
              <div>
                <div style={infoLabelStyle}>{t.details.duration}</div>
                <div style={infoValueStyle}>
                  {duration} {duration > 1 ? t.units.days : t.units.day}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Request Details Section */}
        {(selectedLeave.reason || selectedLeave.handover) && (
          <div style={infoSectionStyle}>
            <div style={sectionHeaderStyle}>
              <FileText size={screenSize === "xs" ? 16 : 18} />
              <span>{t.sections.requestDetails}</span>
            </div>
            <div style={infoGridStyle}>
              {selectedLeave.reason && (
                <div
                  style={{
                    ...infoItemStyle,
                    gridColumn: screenSize !== "xs" ? "1 / -1" : "auto",
                  }}
                >
                  <FileText size={screenSize === "xs" ? 14 : 16} />
                  <div style={{ width: "100%" }}>
                    <div style={infoLabelStyle}>{t.details.reason}</div>
                    <div
                      style={{
                        ...infoValueStyle,
                        background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                        border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                        borderRadius: "8px",
                        padding: "12px",
                        lineHeight: 1.5,
                      }}
                    >
                      {selectedLeave.reason}
                    </div>
                  </div>
                </div>
              )}
              {selectedLeave.handover && (
                <div
                  style={{
                    ...infoItemStyle,
                    gridColumn: screenSize !== "xs" ? "1 / -1" : "auto",
                  }}
                >
                  <User size={screenSize === "xs" ? 14 : 16} />
                  <div style={{ width: "100%" }}>
                    <div style={infoLabelStyle}>{t.details.handover}</div>
                    <div
                      style={{
                        ...infoValueStyle,
                        background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                        border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.05)",
                        borderRadius: "8px",
                        padding: "12px",
                        lineHeight: 1.5,
                      }}
                    >
                      {selectedLeave.handover}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Review Information Section */}
        {(selectedLeave.approver || selectedLeave.approvedBy || selectedLeave.approvedDate) && (
          <div style={infoSectionStyle}>
            <div style={sectionHeaderStyle}>
              <User size={screenSize === "xs" ? 16 : 18} />
              <span>{t.sections.reviewInfo}</span>
            </div>
            <div
              style={{
                background: `linear-gradient(135deg, ${statusColor}15, ${statusColor}05)`,
                border: `2px solid ${statusColor}30`,
                borderRadius: "12px",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Status indicator strip */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "4px",
                  height: "100%",
                  background: statusColor,
                }}
              />

              {/* Approver Avatar */}
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: `linear-gradient(135deg, ${statusColor}, ${theme.colors.primary})`,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#ffffff",
                  fontSize: fonts.body,
                  fontWeight: "600",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                {selectedLeave.approverAvatar ||
                selectedLeave.approverImage ||
                selectedLeave.approverAvatarUrl ? (
                  <img
                    src={
                      selectedLeave.approverAvatar ||
                      selectedLeave.approverImage ||
                      selectedLeave.approverAvatarUrl
                    }
                    alt={selectedLeave.approver}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "inherit",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  (selectedLeave.approver &&
                    getTwoInitials(selectedLeave.approver)) ||
                  "HR"
                )}
              </div>

              {/* Approval Info */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "4px",
                  }}
                >
                  {selectedLeave.status === "approved" && (
                    <CheckCircle size={20} color={theme.colors.success} />
                  )}
                  {selectedLeave.status === "declined" && (
                    <XCircle size={20} color={theme.colors.danger} />
                  )}
                  {selectedLeave.status === "pending" && (
                    <Clock size={20} color={theme.colors.warning} />
                  )}
                  <span
                    style={{
                      color: statusColor,
                      fontSize: fonts.small,
                      fontWeight: "600",
                      textTransform: "uppercase",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {t.status[selectedLeave.status] || selectedLeave.status}
                  </span>
                </div>

                <div
                  style={{
                    color: isDarkMode ? "white" : "#1a1a1a",
                    fontSize: fonts.body,
                    fontWeight: "600",
                    marginBottom: "2px",
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {selectedLeave.approvedBy || t.details.notAssigned}
                </div>

                <div
                  style={{
                    color: isDarkMode
                      ? "rgba(255, 255, 255, 0.6)"
                      : "rgba(26, 26, 26, 0.6)",
                    fontSize: fonts.small,
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  {selectedLeave.approvedDate
                    ? new Date(selectedLeave.approvedDate).toLocaleDateString(
                        language === "ar" ? "ar-EG" : "en-US",
                        { weekday: "short", year: "numeric", month: "short", day: "numeric" }
                      )
                    : t.details.pendingReview}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Actions */}
        <div
          style={{
            display: "flex",
            gap: screenSize === "xs" ? "0.75rem" : "0.75rem",
            justifyContent: screenSize === "xs" ? "center" : "flex-end",
            marginTop: screenSize === "xs" ? "1.5rem" : "2rem",
            paddingTop: spacing.padding,
            borderTop: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
            flexWrap: screenSize === "xs" ? "wrap" : "nowrap",
          }}
        >
          <button
            style={{
              background: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
              color: isDarkMode ? "white" : "#1a1a1a",
              border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.1)",
              padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.25rem",
              borderRadius: screenSize === "xs" ? "0.625rem" : "0.625rem",
              cursor: "pointer",
              fontSize: fonts.body,
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
              transition: "all 0.2s",
              whiteSpace: "nowrap",
              fontFamily: "'Cairo', sans-serif",
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            {t.details.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailsModal;